import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @param {boolean} open
 */
function dispatchHeaderMobileDrawer(open) {
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.headerMobileDrawer, { bubbles: true, detail: { open } })
  );
}

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * Freeze page scroll without `html[scroll-lock]` (overflow on &lt;html&gt; breaks the header’s
   * `position: sticky`). Same pattern as `DialogComponent.showDialog` / `closeDialog`.
   * @type {boolean}
   */
  #rootScrollLocked = false;

  /** @type {number} */
  #rootScrollLockY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    const rootWasOpen = this.refs.details?.hasAttribute('open');
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    if (this.#rootScrollLocked) this.#unlockRootScroll();
    if (rootWasOpen) dispatchHeaderMobileDrawer(false);
  }

  #lockRootScroll() {
    if (this.#rootScrollLocked) return;

    const y = window.scrollY ?? document.documentElement.scrollTop ?? document.body.scrollTop ?? 0;
    this.#rootScrollLockY = y;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    this.#rootScrollLocked = true;
  }

  /**
   * Restore layout scroll; header is notified *after* this via `dispatchHeaderMobileDrawer(false)`
   * so `syncScrollState` reads real `scrollY` (not 0 while body was fixed).
   */
  #unlockRootScroll() {
    if (!this.#rootScrollLocked) return;
    this.#rootScrollLocked = false;

    const htmlEl = document.documentElement;
    const previousScrollBehavior = htmlEl.style.scrollBehavior;
    htmlEl.style.scrollBehavior = 'auto';

    const topStyle = document.body.style.top;
    const parsedTop = Number.parseInt(topStyle, 10);
    const restoreY = Number.isFinite(parsedTop)
      ? Math.max(0, -parsedTop)
      : Math.max(0, this.#rootScrollLockY);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    window.scrollTo(0, restoreY);

    requestAnimationFrame(() => {
      htmlEl.style.scrollBehavior = previousScrollBehavior;
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(/** @type {Event | undefined} */ event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  toggle(event) {
    event?.preventDefault();
    if (this.isOpen) return this.close();
    this.refs.details.open = true;
    return this.open(event);
  }

  /**
   * Pin the fixed sheet to the bottom edge of the real header bar (viewport coords).
   * Using only --header-group-height in CSS leaves a band of page content visible (see menu gap).
   */
  #syncSheetTopFromHeader() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;
    let bottom = Math.round(header.getBoundingClientRect().bottom);
    if (bottom < 1) {
      const raw = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
      bottom = Math.round(Number.parseFloat(raw) || 60);
    }
    this.style.setProperty('--below-header-menu-top', `${Math.max(0, bottom)}px`);
  }

  #clearSheetTop() {
    this.style.removeProperty('--below-header-menu-top');
  }

  /**
   * Below-header layout; sheet position is synced from the header bar on open.
   * @returns {boolean}
   */
  #isBelowHeaderLayout() {
    return true;
  }

  /**
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      // Header must read scrollY before body is fixed (scroll becomes 0 while fixed).
      dispatchHeaderMobileDrawer(true);
      this.#lockRootScroll();
    }

    summary.setAttribute('aria-expanded', 'true');

    if (!this.#isBelowHeaderLayout()) {
      this.#hideHeaderAndBanner();
    } else {
      this.#syncSheetTopFromHeader();
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (this.#isBelowHeaderLayout()) {
        this.#syncSheetTopFromHeader();
      }
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      if (!this.#isBelowHeaderLayout()) {
        this.#showHeaderAndBanner();
      } else {
        this.#clearSheetTop();
      }
    }

    // Release page scrolling independently of descendant animation completion.
    // Imported storefront styles can add infinite animations inside the drawer.
    if (details === this.refs.details) {
      this.#unlockRootScroll();
      removeTrapFocus();
      this.querySelectorAll('details[open]').forEach(reset);
      reset(details);
      dispatchHeaderMobileDrawer(false);
      return;
    }
    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        this.#unlockRootScroll();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            dispatchHeaderMobileDrawer(false);
          });
        });
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    }, { subtree: false });
  }

  #hideHeaderAndBanner() {
    const stickyBanner = document.querySelector('.product-sticky-banner');

    if (stickyBanner instanceof HTMLElement) {
      stickyBanner.style.opacity = '0';
      stickyBanner.style.visibility = 'hidden';
      stickyBanner.style.transition = 'opacity 0.2s ease, visibility 0.2s ease';
    }
  }

  #showHeaderAndBanner() {
    const stickyBanner = document.querySelector('.product-sticky-banner');

    if (stickyBanner instanceof HTMLElement) {
      stickyBanner.style.opacity = '1';
      stickyBanner.style.visibility = 'visible';
      stickyBanner.style.transition = 'opacity 0.2s ease, visibility 0.2s ease';

      setTimeout(() => {
        stickyBanner.style.opacity = '';
        stickyBanner.style.visibility = '';
        stickyBanner.style.transition = '';
      }, 300);
    }
  }

  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
